// Reusable tag chip input with searchable suggestions + free-form custom tags.
// Used by Public Submit, Admin Direct Upload, ZIP Batch Review rows, and the
// Genre Review bulk-fix tool. Mobile-friendly: chips wrap and the input grows.
//
// Behavior:
// - Type → suggestions filtered from `suggestions` + `popularTags`
// - Enter / comma / blur → commit the typed value as a tag
// - Backspace on empty input → remove last chip
// - Click a suggestion → adds it
// - Click ✕ on a chip → removes it
// - Case-insensitive de-dup; first-seen casing preserved.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

function uniqByLower(values) {
  const seen = new Set();
  const out = [];
  for (const v of values || []) {
    const s = String(v || "").trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

export default function TagChipsInput({
  label,
  value = [],
  onChange,
  suggestions = [],
  popularTags = [],
  placeholder = "Type a tag and press Enter…",
  testId = "tag-chips",
  maxItems = 20,
}) {
  const [input, setInput] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const wrapRef = useRef(null);

  const tags = uniqByLower(value);

  const allSuggestions = useMemo(
    () => uniqByLower([...suggestions, ...popularTags.map((p) => p.display || p)]),
    [suggestions, popularTags],
  );

  const filtered = useMemo(() => {
    const q = input.trim().toLowerCase();
    const taken = new Set(tags.map((t) => t.toLowerCase()));
    return allSuggestions
      .filter((s) => !taken.has(s.toLowerCase()))
      .filter((s) => !q || s.toLowerCase().includes(q))
      .slice(0, 8);
  }, [allSuggestions, input, tags]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const commit = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    if (tags.length >= maxItems) return;
    if (tags.some((t) => t.toLowerCase() === v.toLowerCase())) {
      setInput("");
      return;
    }
    onChange?.([...tags, v]);
    setInput("");
  };

  const removeAt = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeAt(tags.length - 1);
    }
  };

  return (
    <div className="space-y-1.5" ref={wrapRef}>
      {label && (
        <label className="overline text-xs text-[#A1A4AB]">{label}</label>
      )}
      <div
        className="flex flex-wrap gap-1.5 input-lux !py-2 min-h-[44px] cursor-text"
        onClick={(e) => {
          // Focus the input only if the user clicked the wrapper itself, not a chip ✕ button.
          if (e.target === e.currentTarget) {
            e.currentTarget.querySelector("input")?.focus();
          }
        }}
        data-testid={testId}
      >
        {tags.map((t, idx) => (
          <span
            key={`${t}-${idx}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 text-[#C5A059] text-xs"
            data-testid={`${testId}-chip-${idx}`}
          >
            {t}
            <button
              type="button"
              onClick={() => removeAt(idx)}
              data-testid={`${testId}-remove-${idx}`}
              className="hover:text-[#FDFBF7]"
              aria-label={`Remove ${t}`}
            >
              <X className="w-3 h-3" strokeWidth={2} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setShowSuggest(true)}
          onBlur={() => setTimeout(() => commit(input), 100)}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-[#FDFBF7] placeholder:text-[#6C707A]"
          data-testid={`${testId}-input`}
        />
      </div>
      {showSuggest && filtered.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={`${testId}-suggestions`}
        >
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => commit(s)}
              className="text-xs px-2 py-0.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]/40 hover:text-[#FDFBF7]"
              data-testid={`${testId}-suggest-${s.toLowerCase().replace(/\s+/g, "-")}`}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
